// src/components/ServiceCategory/ServiceCategorySelector.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useProfile } from "../../context/ProfileContext";
import config from "../../config";

/**
 * ServiceCategorySelector
 * - Respects menu permissions from ProfileContext (isMenuAllowed)
 * - Keeps controlled/uncontrolled behaviour as before
 * - includePetStore controls whether type_id === 3 (store) is shown.
 *   Additionally, Pet Shop (service_type_name === "Pet Shop") is only shown
 *   when POS menu is allowed.
 */
export default function ServiceCategorySelector({
  isMultiple = true,
  selected: controlledSelected,
  onChange: controlledOnChange,
  gridCols = "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  includePetStore = true,
  includeOnlyGrooming = false,
  includeOnlyBoarding = false,
  exclude = { categories: [], serviceTypes: [] },
}) {
  const isControlled = typeof controlledOnChange === "function";

  // normalize controlled selected
  const normalizedControlled = (() => {
    if (!isControlled) return [];
    if (Array.isArray(controlledSelected)) return controlledSelected;
    if (controlledSelected == null) return [];
    return [controlledSelected];
  })();

  const [internalSelected, setInternalSelected] = useState([]);
  const selectedIds = isControlled ? normalizedControlled : internalSelected;
  const emitChange = (nextArray) => {
    if (isControlled) controlledOnChange(nextArray);
    else setInternalSelected(nextArray);
  };

  // profile context for permissions & raw categories
  const { rawServiceCategory, isMenuAllowed } = useProfile();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      setLoading(true);
      let categories = rawServiceCategory || [];

      // filter categories by grooming/boarding flags if requested
      if (includeOnlyGrooming) {
        categories = categories.filter(
          (cat) =>
            cat.type_id === 2 && cat.service_category_name === "Grooming",
        );
      } else if (includeOnlyBoarding) {
        categories = categories.filter(
          (cat) =>
            cat.type_id === 2 && cat.service_category_name === "Boarding",
        );
      }

      if (exclude.categories && exclude.categories.length) {
        categories = categories.filter(
          (cat) => !exclude.categories.includes(cat.service_category_name),
        );
      }
      
      // flatten service types
      const flattened = [];
      categories.forEach((cat) => {
        let serviceTypes = cat.service_type || [];
        // --- NEW: exclude specific service types ---
        if (exclude.serviceTypes && exclude.serviceTypes.length) {
          serviceTypes = serviceTypes.filter(
            (st) => !exclude.serviceTypes.includes(st.service_type_name),
          );
        }
        serviceTypes.forEach((st) => {
          if (st.is_active == 1) {
            flattened.push({
              service_category_id: cat.service_category_id,
              service_type_id: st.service_type_id,
              service_type_name: st.service_type_name,
              service_category_type_id: st.service_category_type_id,
              type_id: cat.type_id,
              service_category_name: cat.service_category_name,
              title: st.service_type_name,
              description: cat.service_category_name || "",
              icon: st?.photo
                ? `${ config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${st.photo}`
                : "/icons/Group 1000003854.svg",
            });
          }
        });
      });

      // map service -> required menu name (same mapping you use elsewhere)
      const serviceToMenuMap = {
        "Grooming Salon": "Grooming Services",
        "Kennel Boarding": "Boarding Services",
        "Pet Resort": "Boarding Services",
        "Day Care Services": "Day Care Services",
        "Pet Vet Clinic": "Vet Clinic Services",
        "Dog Walking Services": "Dog Walking Services",
        "Pet Shop": "POS",
      };

      // filter by permission and includePetStore flag
      const filtered = flattened.filter((item) => {
        // 1) respect includePetStore flag for type_id === 3 (store)
        if (!includePetStore && item.type_id === 3) return false;

        // 2) if this service has a mapped menu, require that permission
        const requiredMenu = serviceToMenuMap[item.service_type_name];
        if (requiredMenu) {
          // Pet Shop: also respects includePetStore but still requires POS permission
          const allowed = isMenuAllowed(requiredMenu);
          return allowed;
        }

        // 3) otherwise allow by default
        return true;
      });

      setServices(filtered);
      setLoading(false);
    } catch (err) {
      setError("Failed to load services");
      setLoading(false);
    }
    // include isMenuAllowed so selector updates when permissions change
  }, [
    rawServiceCategory,
    includePetStore,
    includeOnlyGrooming,
    includeOnlyBoarding,
    isMenuAllowed,
  ]);

  // toggle selection (keeps previous behaviour)
  const handleToggle = (service) => {
    const serviceData = {
      service_category_id: service.service_category_id,
      service_type_id: service.service_type_id,
      service_category_type_id: service.service_category_type_id,
      service_type_name: service.service_type_name,
      type_id: service.type_id,
      service_category_name: service.service_category_name,
    };

    const already = selectedIds.some(
      (item) =>
        item &&
        item.service_category_type_id === service.service_category_type_id,
    );

    const next = isMultiple
      ? already
        ? selectedIds.filter(
            (item) =>
              item &&
              item.service_category_type_id !==
                service.service_category_type_id,
          )
        : [...selectedIds, serviceData]
      : already
        ? []
        : [serviceData];

    emitChange(next);
  };

  if (loading) {
    return (
      <div className="min-h-[240px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center py-4">{error}</div>;
  }

  return (
    <div className={`grid ${gridCols} auto-rows-fr items-stretch gap-4`}>
      {services.map((svc) => {
        const isSelected = selectedIds.some(
          (item) =>
            item &&
            item.service_category_type_id === svc.service_category_type_id,
        );

        return (
          <div
            key={svc.service_category_type_id}
            onClick={() => handleToggle(svc)}
            className={[
              "flex items-center p-1 border rounded-lg transition cursor-pointer gap-2 h-full",
              isSelected
                ? "border-blue-500 bg-blue-50 shadow-lg"
                : "border-gray-200 bg-white hover:border-gray-300",
            ].join(" ")}
          >
            <img src={svc.icon} alt={svc.title} className="w-8 h-8" />

            <div>
              <h3
                className={[
                  "text-sm",
                  isSelected ? "text-primary" : "text-gray-800",
                ].join(" ")}
              >
                {svc.title}
              </h3>
              {svc.description && (
                <p className="text-xs text-gray-500">{svc.description}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

ServiceCategorySelector.propTypes = {
  isMultiple: PropTypes.bool,
  selected: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onChange: PropTypes.func,
  gridCols: PropTypes.string,
  includePetStore: PropTypes.bool,
  includeOnlyGrooming: PropTypes.bool,
  includeOnlyBoarding: PropTypes.bool,
};
